import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Save, Loader2, Plus, Trash2, Edit, X, ChevronUp, ChevronDown } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../../components/ui/dialog";

const CATEGORIES = [
    { value: "occupancy", label: "Doluluk" },
    { value: "hotel_count", label: "Otel Sayısı" },
    { value: "investment", label: "Yatırım" },
    { value: "tourism", label: "Turizm" },
];
const TRENDS = [
    { value: "up", label: "↑ Yükseliş" },
    { value: "down", label: "↓ Düşüş" },
    { value: "stable", label: "→ Sabit" },
];
const DISPLAY_TYPES = [
    { value: "card", label: "Kart" },
    { value: "chart", label: "Grafik" },
    { value: "table", label: "Tablo" },
];
const LANGS = [
    { code: "tr", name: "Türkçe" },
    { code: "en", name: "English" },
    { code: "de", name: "Deutsch" },
];

const emptyTrans = () => ({ tr: "", en: "", de: "" });
const emptyItem = () => ({
    category: "occupancy",
    name: emptyTrans(),
    value: 0,
    unit: "%",
    trend_direction: "stable",
    trend_percentage: 0,
    period: "",
    source_reference: "",
    display_type: "card",
    is_active: true,
    sort_order: 0,
});

export default function AdminSectorData() {
    const [items, setItems] = useState([]);
    const [filterCat, setFilterCat] = useState("");
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const params = filterCat ? { category: filterCat } : {};
            const { data } = await api.get("/admin/sector-data", { params });
            setItems(data.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterCat]);

    const move = async (idx, direction) => {
        const next = [...items];
        const swap = idx + direction;
        if (swap < 0 || swap >= next.length) return;
        [next[idx], next[swap]] = [next[swap], next[idx]];
        const reordered = next.map((it, i) => ({ ...it, sort_order: i }));
        setItems(reordered);
        try {
            await api.put("/admin/sector-data/reorder", {
                items: reordered.map((it, i) => ({ id: it.id, sort_order: i })),
            });
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Veri silinsin mi?")) return;
        try {
            await api.delete(`/admin/sector-data/${id}`);
            toast.success("Silindi");
            setItems((p) => p.filter((it) => it.id !== id));
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-sector-data">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-dark-navy">Sektör Verileri</h1>
                    <p className="text-sm text-deep-blue/70">Doluluk, otel sayısı, yatırım ve turizm verilerini yönetin.</p>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        value={filterCat}
                        onChange={(e) => setFilterCat(e.target.value)}
                        className="text-sm rounded-md border border-slate-300 px-2 py-2"
                        data-testid="sector-filter-category"
                    >
                        <option value="">Tüm kategoriler</option>
                        {CATEGORIES.map((c) => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                    </select>
                    <button
                        onClick={() => setEditing(emptyItem())}
                        className="bg-dark-navy hover:bg-deep-blue text-white text-sm font-semibold px-4 py-2 rounded-md inline-flex items-center gap-2"
                        data-testid="sector-add-btn"
                    >
                        <Plus className="w-4 h-4" /> Yeni
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-100 shadow-md overflow-hidden">
                <table className="w-full text-sm" data-testid="sector-table">
                    <thead className="bg-light-bg text-deep-blue">
                        <tr>
                            <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-semibold w-24">Sıra</th>
                            <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-semibold">Ad</th>
                            <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-semibold">Kategori</th>
                            <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider font-semibold">Değer</th>
                            <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-semibold">Trend</th>
                            <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-semibold">Durum</th>
                            <th className="px-4 py-2.5 w-32"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading ? (
                            <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">Yükleniyor...</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">Veri yok.</td></tr>
                        ) : (
                            items.map((it, idx) => (
                                <tr key={it.id} className="hover:bg-light-bg/50">
                                    <td className="px-4 py-2.5 text-xs text-deep-blue/70">
                                        <div className="inline-flex items-center gap-1">
                                            <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 rounded hover:bg-light-bg disabled:opacity-30">
                                                <ChevronUp className="w-3 h-3" />
                                            </button>
                                            <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="p-1 rounded hover:bg-light-bg disabled:opacity-30">
                                                <ChevronDown className="w-3 h-3" />
                                            </button>
                                            <span className="font-data">{it.sort_order}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-2.5 font-medium text-dark-navy">{it.name?.tr || "—"}</td>
                                    <td className="px-4 py-2.5 text-deep-blue/80">
                                        {CATEGORIES.find((c) => c.value === it.category)?.label || it.category}
                                    </td>
                                    <td className="px-4 py-2.5 text-right font-data font-semibold text-dark-navy">
                                        {it.value} {it.unit}
                                    </td>
                                    <td className="px-4 py-2.5">
                                        <span className={`text-xs font-data font-semibold ${it.trend_direction === "up" ? "text-data-green" : it.trend_direction === "down" ? "text-data-red" : "text-deep-blue/60"}`}>
                                            {it.trend_direction === "up" ? "↑" : it.trend_direction === "down" ? "↓" : "→"} {it.trend_percentage}%
                                        </span>
                                    </td>
                                    <td className="px-4 py-2.5">
                                        <span className={`text-xs px-2 py-0.5 rounded ${it.is_active ? "bg-data-green/10 text-data-green" : "bg-slate-200 text-slate-500"}`}>
                                            {it.is_active ? "Aktif" : "Pasif"}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2.5 text-right">
                                        <button
                                            onClick={() => setEditing(it)}
                                            className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded text-deep-blue hover:bg-light-bg"
                                            data-testid={`sector-edit-${it.id}`}
                                        >
                                            <Edit className="w-3 h-3" /> Düzenle
                                        </button>
                                        <button
                                            onClick={() => handleDelete(it.id)}
                                            className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded text-data-red hover:bg-red-50"
                                            data-testid={`sector-delete-${it.id}`}
                                        >
                                            <Trash2 className="w-3 h-3" /> Sil
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {editing && (
                <SectorEditDialog
                    item={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null);
                        fetchItems();
                    }}
                />
            )}
        </div>
    );
}

function SectorEditDialog({ item, onClose, onSaved }) {
    const [form, setForm] = useState({ ...emptyItem(), ...item });
    const [saving, setSaving] = useState(false);

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
    const updateName = (lang, v) =>
        setForm((p) => ({ ...p, name: { ...(p.name || emptyTrans()), [lang]: v } }));

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                category: form.category,
                name: form.name,
                value: parseFloat(form.value) || 0,
                unit: form.unit,
                trend_direction: form.trend_direction,
                trend_percentage: parseFloat(form.trend_percentage) || 0,
                source_reference: form.source_reference,
                display_type: form.display_type,
                is_active: !!form.is_active,
                sort_order: parseInt(form.sort_order) || 0,
            };
            if (form.id) {
                await api.put(`/admin/sector-data/${form.id}`, payload);
            } else {
                await api.post("/admin/sector-data", payload);
            }
            toast.success("Kaydedildi");
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{form.id ? "Veriyi Düzenle" : "Yeni Veri"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <Tabs defaultValue="tr">
                        <TabsList className="bg-light-bg">
                            {LANGS.map((l) => (
                                <TabsTrigger
                                    key={l.code}
                                    value={l.code}
                                    className="data-[state=active]:bg-dark-navy data-[state=active]:text-white"
                                    data-testid={`sector-name-tab-${l.code}`}
                                >
                                    {l.name}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                        {LANGS.map((l) => (
                            <TabsContent key={l.code} value={l.code} className="pt-3">
                                <Field label={`Ad (${l.code.toUpperCase()})`}>
                                    <input
                                        value={form.name?.[l.code] || ""}
                                        onChange={(e) => updateName(l.code, e.target.value)}
                                        className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-teal"
                                        data-testid={`sector-name-${l.code}`}
                                    />
                                </Field>
                            </TabsContent>
                        ))}
                    </Tabs>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <Field label="Kategori">
                            <select
                                value={form.category}
                                onChange={(e) => update("category", e.target.value)}
                                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none bg-white"
                                data-testid="sector-category-select"
                            >
                                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                        </Field>
                        <Field label="Değer">
                            <input
                                type="number"
                                step="0.01"
                                value={form.value}
                                onChange={(e) => update("value", e.target.value)}
                                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none font-data"
                                data-testid="sector-value"
                            />
                        </Field>
                        <Field label="Birim">
                            <input
                                value={form.unit}
                                onChange={(e) => update("unit", e.target.value)}
                                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none"
                            />
                        </Field>
                        <Field label="Trend">
                            <select
                                value={form.trend_direction}
                                onChange={(e) => update("trend_direction", e.target.value)}
                                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none bg-white"
                            >
                                {TRENDS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </Field>
                        <Field label="Trend %">
                            <input
                                type="number"
                                step="0.1"
                                value={form.trend_percentage}
                                onChange={(e) => update("trend_percentage", e.target.value)}
                                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none font-data"
                            />
                        </Field>
                        <Field label="Görünüm Tipi">
                            <select
                                value={form.display_type}
                                onChange={(e) => update("display_type", e.target.value)}
                                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none bg-white"
                            >
                                {DISPLAY_TYPES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                            </select>
                        </Field>
                    </div>

                    <Field label="Kaynak Referansı">
                        <input
                            value={form.source_reference}
                            onChange={(e) => update("source_reference", e.target.value)}
                            className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none"
                            placeholder="Örn. TÜRSAB, KTB, TÜİK..."
                        />
                    </Field>

                    <label className="inline-flex items-center gap-2 text-sm text-dark-navy">
                        <input
                            type="checkbox"
                            checked={!!form.is_active}
                            onChange={(e) => update("is_active", e.target.checked)}
                            data-testid="sector-active-toggle"
                        />
                        Aktif
                    </label>
                </div>
                <DialogFooter>
                    <button
                        onClick={onClose}
                        className="text-sm px-4 py-2 rounded-md border border-slate-300 hover:bg-light-bg"
                        type="button"
                    >
                        İptal
                    </button>
                    <button
                        onClick={save}
                        disabled={saving}
                        className="bg-dark-navy text-white text-sm font-semibold px-4 py-2 rounded-md inline-flex items-center gap-2 disabled:opacity-70"
                        data-testid="sector-save-btn"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Kaydet
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-xs font-semibold text-deep-blue/80 uppercase tracking-wider">{label}</span>
            <div className="mt-1.5">{children}</div>
        </label>
    );
}
